import { ref, watch, computed, type ComputedRef, type CSSProperties } from 'vue'
import {
  hasLightingEditableField,
  LIGHTING_EDITABLE_FIELD,
  LIGHTING_TARGET,
  type lightingColor,
  type LightingConfigData,
  type LightingEffectDefinition,
  type RGB,
  LightingDomainType,
} from '@rapoo/device-sdk'
import { defaultReportError } from '@/composables/useErrorLogger'
import { useLoading } from '@/composables/useLoading'
import {
  useLightingSDK,
  beginLatest,
  createLatestCommitter,
  useLightingCapability,
  useLightingLifecycle,
} from './useLightingSDK'

/**
 * RGB 指示灯（LIGHTING_TARGET.Indicator）业务。
 *
 * - 点选只切换编辑对象，不写 setCurrentEffect
 * - indicatorPreviewRgb / indicatorWidgetStyles 供键盘部件发光
 * - 不监听亮度/速度上报（上报属按键灯）
 */

export type UseIndicatorLightingOptions = {
  autoInit?: boolean
}

const INDICATOR_DOMAIN = LightingDomainType.Indicator

export function useIndicatorLighting(options: UseIndicatorLightingOptions = {}) {
  const { autoInit = true } = options
  const { isLoading, withLoading } = useLoading()
  const { currentDevice, hasIndicator } = useLightingCapability()

  const { getEffects, getConfig, setConfig, getLighting } = useLightingSDK()

  const rgb = ref<RGB>({ r: 255, g: 255, b: 255 })
  const effects = ref<LightingEffectDefinition[]>([])
  const currentMode = ref(-1)
  const brightness = ref(60)
  /** null = 不显示部件发光 */
  const indicatorPreviewRgb = ref<RGB | null>(null)

  const effectConfRequestId = ref(0)
  const initRequestId = ref(0)

  const currentEffectInfo = computed(() =>
    effects.value.find((e) => e.id === currentMode.value),
  ) as ComputedRef<LightingEffectDefinition | undefined>

  const indicatorWidgetStyles = computed<CSSProperties | undefined>(() => {
    if (!indicatorPreviewRgb.value) return undefined
    const { r, g, b } = indicatorPreviewRgb.value
    return { boxShadow: `0px 9px 9px -7px rgb(${r}, ${g}, ${b})` }
  })

  const syncPreview = (color?: RGB | null) => {
    indicatorPreviewRgb.value = color ? { ...color } : null
  }

  const resetUi = () => {
    effects.value = []
    currentMode.value = -1
  }

  const applyConfig = (data: LightingConfigData) => {
    brightness.value = data.brightness ?? 60
    rgb.value = data.rgb ?? rgb.value
    syncPreview(data.rgb ?? rgb.value)
  }

  const loadEffectConf = async (index: number, stillWanted?: () => boolean) => {
    const latest = beginLatest(effectConfRequestId)
    // 调用方可声明「结果已无人需要」（亮度连击中的旧回读），避免旧值闪回滑块
    const task = { alive: () => latest.alive() && (stillWanted?.() ?? true) }
    if (!getLighting() || index < 0) return
    try {
      const data = await getConfig(INDICATOR_DOMAIN, index)
      if (!task.alive() || !data) return
      applyConfig(data)
    } catch (error) {
      console.error('Failed to load indicator lighting config:', error)
      defaultReportError(`Failed to load indicator lighting config: ${JSON.stringify(error)}`)
    }
  }

  /** 预热部件预览色（默认氛围灯 id=1），不改 currentMode */
  const hydratePreview = async () => {
    if (!getLighting() || !hasIndicator.value) return
    try {
      const list = await getEffects(LIGHTING_TARGET.Indicator)
      const targetId = list.find((e) => e.id === 1)?.id ?? list[0]?.id
      if (targetId === undefined) return
      const data = await getConfig(INDICATOR_DOMAIN, targetId)
      if (data?.rgb) syncPreview(data.rgb)
    } catch (err) {
      console.warn('Failed to hydrate indicator preview:', err)
    }
  }

  const init = async (opts?: { force?: boolean }) => {
    if (!currentDevice.value || !getLighting() || !hasIndicator.value) return

    const task = beginLatest(initRequestId)
    effectConfRequestId.value++

    await withLoading(
      async () => {
        try {
          effects.value = await getEffects(LIGHTING_TARGET.Indicator)
          if (!task.alive()) return
          currentMode.value = effects.value[0]?.id ?? -1
          if (!task.alive()) return
          await loadEffectConf(currentMode.value)
        } catch (err) {
          console.error('Failed to initialize indicator lighting:', err)
          defaultReportError(`Failed to initialize indicator lighting: ${JSON.stringify(err)}`)
        }
      },
      { skipIfLoading: opts?.force ? false : true },
    )
  }

  const setMode = async (mode?: LightingEffectDefinition) => {
    if (!getLighting() || !mode) return
    currentMode.value = mode.id
    await withLoading(async () => {
      try {
        await loadEffectConf(mode.id)
      } catch (err) {
        console.error('Failed to set indicator mode:', err)
        defaultReportError(`Failed to set indicator mode: ${JSON.stringify(err)}`)
      }
    })
  }

  const setColor = async (_color?: lightingColor) => {
    if (!getLighting()) return
    if (!hasLightingEditableField(currentEffectInfo.value, LIGHTING_EDITABLE_FIELD.Color)) return

    syncPreview(rgb.value)
    await withLoading(async () => {
      try {
        await setConfig(INDICATOR_DOMAIN, currentMode.value, { rgb: rgb.value })
      } catch (error) {
        console.error('Failed to set indicator color:', error)
        defaultReportError(`Failed to set indicator color: ${JSON.stringify(error)}`)
      } finally {
        await loadEffectConf(currentMode.value)
      }
    })
  }

  /** 亮度合并写入：连点只落最后一次；中间轮不回读，避免旧值闪回 UI */
  const commitBrightness = createLatestCommitter<'brightness', number>(
    async (_key, value, { hasNewer, settle }) => {
      try {
        await setConfig(INDICATOR_DOMAIN, currentMode.value, { brightness: value })
      } catch (err) {
        console.error('Failed to set indicator brightness:', err)
        defaultReportError(`Failed to set indicator brightness: ${JSON.stringify(err)}`)
      } finally {
        // 连击中先静默一小段；期间来了更新值就不回读，交给最后一轮
        if (!(await settle())) await loadEffectConf(currentMode.value, () => !hasNewer())
      }
    },
  )

  const setBrightness = async (value?: number) => {
    if (!getLighting()) return
    if (value !== undefined) brightness.value = value
    await commitBrightness('brightness', brightness.value)
  }

  watch(rgb, (color) => syncPreview(color), { deep: true })

  useLightingLifecycle({
    autoInit,
    onDevice: () => syncPreview(null),
    reset: resetUi,
    init,
    onInvalidate: () => {
      initRequestId.value++
      effectConfRequestId.value++
    },
  })

  return {
    isLoading,
    hasIndicator,
    effects,
    currentMode,
    currentEffectInfo,
    rgb,
    brightness,
    indicatorPreviewRgb,
    indicatorWidgetStyles,
    init,
    hydratePreview,
    setMode,
    setColor,
    setBrightness,
  }
}
